import { motion } from "framer-motion";
import { fadeUp, easeExpo } from "@/lib/motion";

const NETWORK = [
  {
    code: "HK",
    name: "Hong Kong",
    city: "Trade desk",
    role: "Global sourcing & logistics",
    lanes: ["Bonded routing", "Free-port fulfilment"],
  },
  {
    code: "SG",
    name: "Singapore",
    city: "Regional desk",
    role: "APAC procurement",
    lanes: ["ASEAN OEMs", "Indo-Pacific reach"],
  },
];

export default function GlobalTradingNetwork() {
  return (
    <section
      id="network"
      className="relative py-28 md:py-40"
      data-testid="section-network"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <SectionHeader
          index="01"
          overline="Global Trading Network"
          title={
            <>
              Two desks. <br />
              One <span className="text-electric-500">exchange</span>.
            </>
          }
          copy="Operating desks in Hong Kong and Singapore, with sourcing reach across China, India and Asia-Pacific. A single quote-to-delivery workflow — a requisition sent today becomes a quoted shipment in under 24 hours."
        />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4">
          {NETWORK.map((n, i) => (
            <motion.article
              key={n.code}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: easeExpo, delay: i * 0.06 }}
              className="glass rounded-2xl p-6 md:p-7 relative overflow-hidden group"
              data-testid={`network-card-${n.code.toLowerCase()}`}
            >
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at 100% 0%, rgba(0,229,255,0.10), transparent 60%)",
                  transition: "opacity 500ms cubic-bezier(0.16,1,0.3,1)",
                }}
              />
              <div className="flex items-start justify-between">
                <div className="text-[11px] font-mono tracking-[0.22em] uppercase text-slate-500">
                  Node · {n.code}
                </div>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-electric-500 opacity-70" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-electric-500" />
                </span>
              </div>
              <div className="mt-8 font-display text-4xl md:text-5xl tracking-tight text-white">
                {n.name}
              </div>
              <div className="mt-2 text-slate-400 text-sm">
                {n.city} · <span className="text-slate-300">{n.role}</span>
              </div>
              <div className="mt-6 pt-4 border-t border-white/5 flex flex-wrap gap-x-3 gap-y-1">
                {n.lanes.map((lane) => (
                  <span
                    key={lane}
                    className="text-[11px] font-mono tracking-wider text-electric-500/80"
                  >
                    {lane}
                  </span>
                ))}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SectionHeader({ index, overline, title, copy }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
      <div className="lg:col-span-1 flex items-start">
        <div className="text-[11px] font-mono tracking-[0.22em] uppercase text-electric-500">
          {index}
        </div>
      </div>
      <div className="lg:col-span-6">
        <motion.div
          {...fadeUp}
          className="text-[11px] font-mono tracking-[0.22em] uppercase text-slate-400 mb-4"
        >
          / {overline}
        </motion.div>
        <motion.h2
          {...fadeUp}
          transition={{ duration: 1, ease: easeExpo, delay: 0.05 }}
          className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-[-0.03em] leading-[0.98] text-white"
        >
          {title}
        </motion.h2>
      </div>
      <div className="lg:col-span-5">
        <motion.p
          {...fadeUp}
          transition={{ duration: 1, ease: easeExpo, delay: 0.1 }}
          className="text-slate-300 text-[15px] md:text-base leading-relaxed max-w-[440px]"
        >
          {copy}
        </motion.p>
      </div>
    </div>
  );
}
